import React, { useEffect, useState } from 'react';
import { apiKeysAPI } from '../utils/api';
import { Key, Copy, Trash2, CheckCircle, AlertCircle, X } from 'lucide-react';

type KeyItem = { id: number; is_active: number; createdAt: string };

const ApiKeysModal: React.FC<{ open: boolean; onClose: () => void }>= ({ open, onClose }) => {
  const [keys, setKeys] = useState<KeyItem[]>([]);
  const [issuedKey, setIssuedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await apiKeysAPI.getKeys();
        setKeys(res.data.keys || []);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'API 키 목록을 불러오지 못했습니다.');
      }
    })();
  }, [open]);

  const issueKey = async () => {
    setLoading(true); setError(null); setIssuedKey(null);
    try {
      const res = await apiKeysAPI.issue();
      setIssuedKey(res.data.apiKey);
      const list = await apiKeysAPI.getKeys();
      setKeys(list.data.keys || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'API 키 발급에 실패했습니다.');
    } finally { setLoading(false); }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const revokeKey = async (id: number) => {
    setLoading(true); setError(null);
    try {
      await apiKeysAPI.revokeKey(id);
      const list = await apiKeysAPI.getKeys();
      setKeys(list.data.keys || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'API 키 철회에 실패했습니다.');
    } finally { setLoading(false); }
  };

  if (!open) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <Key className="w-6 h-6 text-purple-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">API 키 관리</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800">오류</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Issue New Key Section */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">새 API 키 발급</h3>
            <p className="text-sm text-gray-600 mb-4">
              외부 애플리케이션에서 Pulse API를 사용하기 위한 키를 발급받습니다.
              발급된 키는 한 번만 표시되므로 안전한 곳에 보관하세요.
            </p>
            <button 
              onClick={issueKey} 
              disabled={loading || keys.some(k => k.is_active === 1)}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Key className="w-4 h-4 mr-2" />
              {loading ? '발급 중...' : '새 키 발급'}
            </button>
            {keys.some(k => k.is_active === 1) && (
              <p className="text-xs text-orange-600 mt-2">
                ⚠️ 이미 활성화된 키가 있습니다. 기존 키를 철회한 후 새 키를 발급하세요.
              </p>
            )}
          </div>

          {/* Issued Key Display */}
          {issuedKey && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start mb-4">
                <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-800">API 키가 발급되었습니다!</p>
                  <p className="text-sm text-green-700 mt-1">
                    아래 키는 <strong>한 번만</strong> 표시됩니다. 지금 복사하여 안전한 곳에 보관하세요.
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-300">
                <div className="flex items-center justify-between">
                  <code className="text-sm font-mono text-gray-800 break-all flex-1 mr-4">
                    {issuedKey}
                  </code>
                  <button
                    onClick={() => copyToClipboard(issuedKey)}
                    className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex-shrink-0"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        복사됨!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        복사
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* API Keys List */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">내 API 키</h3>
            {keys.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                <Key className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">발급된 API 키가 없습니다.</p>
                <p className="text-sm text-gray-400 mt-1">위에서 새 키를 발급받으세요.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {keys.map(k => (
                  <div key={k.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <Key className="w-5 h-5 text-gray-400 mr-3" />
                        <div className="flex-1">
                          <div className="flex items-center">
                            <span className="font-mono text-sm text-gray-700">API Key #{k.id}</span>
                            <span className={`ml-3 px-2 py-1 rounded-full text-xs font-medium ${
                              k.is_active === 1 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {k.is_active === 1 ? '활성' : '비활성'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            발급일: {new Date(k.createdAt).toLocaleString('ko-KR')}
                          </p>
                          <p className="text-xs text-gray-500">
                            일일 할당량: 10,000 요청
                          </p>
                        </div>
                      </div>
                      {k.is_active === 1 && (
                        <button 
                          onClick={() => revokeKey(k.id)} 
                          disabled={loading}
                          className="flex items-center px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          철회
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* API Documentation Link */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>💡 API 사용법:</strong> 좌측 사이드바의 "📖 API 문서" 메뉴에서 자세한 사용법을 확인하세요.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeysModal;




